import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import "moment/locale/en-gb";
import { Calendar, EventProps, momentLocalizer } from "react-big-calendar";
import "@assets/css/calendar-sass/styles.scss";
import "@assets/css/custom-calendar.css";
import { theme } from "@src/theme";
import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import Spinner from "@src/components/Spinner";
import {
  ScheduleOverviewHeader,
  ScheduleOverviewHeaderLeft,
  ButtonsContainer,
  WeeksContainer,
  WeekButton,
  ScheduleOverviewHeaderCenter,
  ScheduleTitleRow,
  SchedulePageTitle,
  ScheduleNewButton,
  CalendarContainer,
  ToggleViewButton,
  SubmittedPlaylistLink,
  SubmittedShowSearchLink,
} from "./ScheduleCalendar.styles";
import {
  useApiEventsQuery,
  useApiMasterScheduleEventsQuery,
  useSubmittedPlaylistsByShowQuery,
} from "@src/queries/scheduleQueries";
import {
  CalendarEvent,
  IMasterScheduleEvent,
  IScheduleEvent,
} from "@src/types/schedules";
import ContentDrawer from "@src/components/ContentDrawer";
import AddNewSchedule from "./AddNewSchedule";
import CuratorScheduleView from "./CuratorScheduleView";
import Tooltip from "@src/components/Tooltip";
import ShowDetailsTooltip from "./ShowDetailsTooltip";
import ListIcon from "@assets/img/compacts-icon.svg";
import { useAppDispatch, useAppSelector } from "@src/redux/hooks";
import {
  resetCreateEventForm,
  resetUpdateEventForm,
  setCreateEventForm,
  setUpdateEventForm,
} from "@src/redux/slices/scheduleSlice";
import { useRole, useAuthCurator } from "@src/hooks/useAuth";
import { FEATURES } from "@src/constants";
import { getFrequencyBadge } from "@src/utils/scheduleFrequency";

const localizer = momentLocalizer(moment);

// Clickable submitted-playlist id shown on a red schedule slot. Opens the
// playlist (/playlist/:documentId) in a new tab. Clicks are kept from bubbling
// so the calendar's own event handler (edit drawer) doesn't also fire.
const PlaylistIdLink = ({
  id,
  documentId,
}: {
  id: string;
  documentId: string;
}): React.JSX.Element => (
  <SubmittedPlaylistLink
    href={`/playlist/${documentId}`}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e): void => e.stopPropagation()}
  >
    {id}
  </SubmittedPlaylistLink>
);

// Clickable "N playlists submitted" label. Opens the playlists page filtered by
// this show (/playlists?showId=…) in a new tab. Clicks are kept from bubbling so
// the calendar's own event handler (edit drawer) doesn't also fire.
const PlaylistShowSearchLink = ({
  showDocumentId,
  label,
}: {
  showDocumentId: string;
  label: string;
}): React.JSX.Element => (
  <SubmittedShowSearchLink
    href={`/playlists?showId=${encodeURIComponent(showDocumentId)}`}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e): void => e.stopPropagation()}
  >
    {label}
  </SubmittedShowSearchLink>
);

export type FilterEventsFormValues = {
  dayOfWeek?: string;
  byCuratorId?: string;
};

const ScheduleCalendar = (): React.JSX.Element => {
  const roleType = useRole();
  const curator = useAuthCurator();
  const dispatch = useAppDispatch();
  const { createEventForm, updateEventForm } = useAppSelector(
    (state) => state.schedule
  );

  const [createEventDrawer, setCreateEventDrawer] = useState(false);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [weekIndexActive, setWeekIndexActive] = useState(0);
  const [showFullCalendar, setShowFullCalendar] = useState(false);
  const scrollToTime = moment().startOf("day").add(8, "hours").toDate();
  const setActiveWeek = (index: number): void => {
    setWeekIndexActive(index);
  };
  const getRangeForWeeks = (): { start: Date; end: Date } => {
    const startOfRange = moment()
      .startOf("isoWeek")
      .add(weekIndexActive, "weeks");
    const endOfRange = startOfRange.clone().add(7, "days");
    return { start: startOfRange.toDate(), end: endOfRange.toDate() };
  };
  const range = getRangeForWeeks();
  const {
    isLoading: loading,
    data: eventsResponse,
    refetch,
  } = useApiEventsQuery(range.start, range.end);

  const { data: masterEventsResponse, isLoading: masterEventsLoading } =
    useApiMasterScheduleEventsQuery(range.start, range.end);
  const masterEvents = useMemo(() => {
    return masterEventsResponse?.data?.data || [];
  }, [masterEventsResponse]);

  const eventsFetched = useMemo(
    () => eventsResponse?.data?.data || [],
    [eventsResponse]
  );

  // Distinct shows that need a submitted-playlist lookup so their calendar box
  // can show how many playlists were submitted:
  //  - "red" schedule slots (scheduled in Strapi but with no attached playlist)
  //  - master-schedule shows (appear on the calendar but have no scheduled item
  //    in Strapi, so they never carry a playlist either)
  const redShowDocumentIds = useMemo(() => {
    const scheduledIds = eventsFetched
      .filter(
        (event: IScheduleEvent) =>
          !event.playlist?.documentId && !!event.show?.documentId
      )
      .map((event: IScheduleEvent) => event.show.documentId);
    const masterIds = masterEvents
      .map((master) => master?.document_id)
      .filter((id): id is string => !!id);
    return Array.from(new Set([...scheduledIds, ...masterIds])).sort();
  }, [eventsFetched, masterEvents]);

  const {
    data: submittedPlaylistsResponse,
    isLoading: submittedPlaylistsLoading,
  } = useSubmittedPlaylistsByShowQuery(redShowDocumentIds);

  // Map of show documentId -> submitted playlists (id + documentId) for that show.
  const submittedPlaylistsByShow = useMemo(() => {
    const map = new Map<string, { id: string; documentId: string }[]>();
    const submitted = submittedPlaylistsResponse?.data?.data || [];
    submitted.forEach((playlist) => {
      const showDocumentId = playlist.show?.documentId;
      if (!showDocumentId) return;
      const existing = map.get(showDocumentId) || [];
      existing.push({
        id: playlist.id.toString(),
        documentId: playlist.documentId ?? "",
      });
      map.set(showDocumentId, existing);
    });
    return map;
  }, [submittedPlaylistsResponse]);

  useEffect(() => {
    const fetchEvents = async (): Promise<void> => {
      if (!eventsFetched || masterEventsLoading) return;

      const formattedEvents = eventsFetched.map((event: IScheduleEvent) => {
        const start = moment(event.startTime).toDate();
        const rawEnd = moment(event.endTime);
        const end =
          rawEnd.format("HH:mm") === "00:00"
            ? rawEnd.subtract(1, "minute").toDate()
            : rawEnd.toDate();

        const submittedPlaylists = event.show?.documentId
          ? submittedPlaylistsByShow.get(event.show.documentId) || []
          : [];

        return {
          id: event.id,
          title: event?.show?.title || "No Show Scheduled",
          scheduleDocumentId: event.documentId,
          playlistDocumentId: event.playlist?.documentId ?? "",
          showDocumentId: event.show?.documentId ?? "",
          curatorName:
            event.playlist?.curator?.name ||
            event.show?.curators?.[0]?.name ||
            "",
          frequency: event.show?.frequency ?? null,
          playlistId: event.playlist?.id?.toString() ?? "",
          playlistTitle: event.playlist?.title || "",
          noPlaylist: !event.playlist?.documentId,
          noShow: !event.show?.documentId,
          submittedPlaylistCount: submittedPlaylists.length,
          submittedPlaylistId:
            submittedPlaylists.length === 1 ? submittedPlaylists[0].id : "",
          submittedPlaylistDocumentId:
            submittedPlaylists.length === 1
              ? submittedPlaylists[0].documentId
              : "",
          start,
          end,
          eventType: "schedule" as "schedule",
        };
      });

      const scheduledSlots = new Set(
        formattedEvents.map((e) => getTimeslotKey(e.start))
      );
      const upComingEvents: CalendarEvent[] = [];
      const formattedMasterEvents = masterEvents
        .filter((master) => {
          const next = master?.next_schedule;
          if (!next) return false;
          return !scheduledSlots.has(getTimeslotKey(next));
        })
        .map((master) => {
          const submittedPlaylists = master?.document_id
            ? submittedPlaylistsByShow.get(master.document_id) || []
            : [];
          const upComingEventString = master?.upcoming_schedules;
          if (upComingEventString) {
            generateUpComingEvents(
              upComingEvents,
              master,
              scheduledSlots,
              submittedPlaylists
            );
          }
          const start = moment(master.next_schedule).toDate();
          const rawEnd = moment(master.next_schedule).add(2, "hours");
          const end =
            rawEnd.format("HH:mm") === "00:00"
              ? rawEnd.subtract(1, "minute").toDate()
              : rawEnd.toDate();
          return {
            id: `master-${master.id}`,
            title: master.title || "Scheduled (Next)",
            scheduleDocumentId: "",
            playlistDocumentId: "",
            showDocumentId: master?.document_id ?? "",
            curatorName: master?.curator ?? "",
            frequency: master?.frequency ?? null,
            playlistId: "",
            playlistTitle: "",
            noPlaylist: true,
            noShow: false,
            submittedPlaylistCount: submittedPlaylists.length,
            submittedPlaylistId:
              submittedPlaylists.length === 1 ? submittedPlaylists[0].id : "",
            submittedPlaylistDocumentId:
              submittedPlaylists.length === 1
                ? submittedPlaylists[0].documentId
                : "",
            start,
            end,
            eventType: "masterSchedule" as "masterSchedule",
          };
        });
      setEvents([
        ...formattedEvents,
        ...formattedMasterEvents,
        ...upComingEvents,
      ]);
    };

    if (!loading && !masterEventsLoading) {
      fetchEvents();
    }
  }, [
    weekIndexActive,
    eventsFetched,
    masterEvents,
    loading,
    masterEventsLoading,
    submittedPlaylistsByShow,
  ]);

  const isCuratorOwnEvent = (event: CalendarEvent): boolean =>
    roleType === "curator" &&
    !!curator.name &&
    event.curatorName === curator.name;

  const CustomEvent = ({ event }: EventProps<CalendarEvent>): JSX.Element => {
    const isMine = isCuratorOwnEvent(event);
    // Show-details hover tooltip: only for real scheduled shows (master/upcoming
    // events carry a master-schedule id, not a show id). Fetch is deferred until
    // the anchor is first hovered so nothing loads for un-hovered shows.
    const [detailsHovered, setDetailsHovered] = useState(false);
    const canShowDetails =
      event.eventType === "schedule" && !event.noShow && !!event.showDocumentId;
    let showColor = !event.playlistId
      ? theme.colors.red
      : event.noShow
        ? theme.colors.gray
        : theme.colors.yellow;
    // "Red" box: a slot with no playlist attached. This covers both a real
    // scheduled slot (eventType "schedule") and a master-schedule show that
    // appears on the calendar without a scheduled item in Strapi (eventType
    // "masterSchedule"). Both show how many playlists have been submitted for
    // their show instead of the empty id row.
    const isRedScheduleBox =
      (event.eventType === "schedule" ||
        event.eventType === "masterSchedule") &&
      event.noPlaylist;
    const submittedCount = event.submittedPlaylistCount ?? 0;
    // Only admins/managers can click through to the playlist; everyone else
    // still sees the id, just as plain text.
    const canOpenPlaylist = roleType === "admin" || roleType === "manager";
    const showPlaylistIdLink =
      canOpenPlaylist && !!event.submittedPlaylistDocumentId;
    const showPlaylistSearchLink = canOpenPlaylist && !!event.showDocumentId;

    // Counts aren't known until the submitted-playlists query resolves, so show
    // a neutral placeholder rather than a false "No playlists submitted".
    const renderSubmittedLabel = (): React.ReactNode => {
      if (submittedPlaylistsLoading) return "—";
      if (submittedCount === 0) return "No playlists submitted";
      if (submittedCount > 1) {
        const label = `${submittedCount} playlists submitted`;
        // showPlaylistSearchLink already guarantees event.showDocumentId is set.
        return showPlaylistSearchLink && event.showDocumentId ? (
          <PlaylistShowSearchLink
            showDocumentId={event.showDocumentId}
            label={label}
          />
        ) : (
          label
        );
      }
      // Exactly one: same label either way, linked only when allowed.
      const playlistId = event.submittedPlaylistId ?? "";
      return (
        <>
          {"1 playlist submitted - "}
          {showPlaylistIdLink ? (
            <PlaylistIdLink
              id={playlistId}
              documentId={event.submittedPlaylistDocumentId ?? ""}
            />
          ) : (
            playlistId
          )}
        </>
      );
    };

    // Line 1 now carries the frequency (e.g. "4w") next to the show title, and —
    // only while the slot still has no playlist scheduled — a "t-N" countdown of
    // days from today until the show. The countdown is the "needs a playlist"
    // clock: it disappears once a playlist is attached or the show has passed.
    const frequencyBadge = getFrequencyBadge(event.frequency);
    const frequencyLabel = frequencyBadge
      ? frequencyBadge === "N/a"
        ? frequencyBadge
        : `${frequencyBadge}w`
      : "";
    const daysUntilShow = moment(event.start)
      .startOf("day")
      .diff(moment().startOf("day"), "days");
    const showDeadlineCountdown =
      event.noPlaylist && !event.noShow && daysUntilShow >= 0;
    // Submission deadline: 4 days out for weekly (1w) shows, 7 for everything
    // else. Green while there's runway, red once inside the deadline window.
    const deadlineThreshold = frequencyBadge === "1" ? 4 : 7;
    const countdownColor =
      daysUntilShow <= deadlineThreshold
        ? theme.colors.red
        : theme.colors.green;

    const titleNode = (
      <strong
        style={{
          fontSize: isMine ? "13px" : "12px",
          color: isMine ? theme.colors.yellow : showColor,
          textShadow: isMine ? `0 0 1px ${theme.colors.yellow}` : "none",
        }}
      >
        {event.title}
        {(frequencyLabel || showDeadlineCountdown) && (
          <span style={{ fontWeight: 400, color: theme.colors.gray }}>
            {" ("}
            {frequencyLabel}
            {frequencyLabel && showDeadlineCountdown ? " · " : ""}
            {showDeadlineCountdown && (
              <span style={{ fontWeight: 700, color: countdownColor }}>
                {`t-${daysUntilShow}`}
              </span>
            )}
            {")"}
          </span>
        )}
      </strong>
    );
    return (
      <div
        style={{
          padding: "5px",
          display: "block",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          backgroundColor: "transparent",
          color: theme.colors.gray,
        }}
      >
        {canShowDetails ? (
          <Tooltip
            delayShow={3000}
            clickable
            float={false}
            afterShow={() => setDetailsHovered(true)}
            contentStyle={{
              backgroundColor: theme.colors.darkerBg,
              opacity: 1,
              padding: "10px",
            }}
            content={
              <ShowDetailsTooltip
                showDocumentId={event.showDocumentId ?? ""}
                enabled={detailsHovered}
              />
            }
          >
            {titleNode}
          </Tooltip>
        ) : (
          titleNode
        )}
        <div
          style={{
            fontSize: "10px",
            fontWeight: 700,
            color: isMine ? theme.colors.white : "inherit",
          }}
        >
          {event.curatorName}
        </div>
        <div
          style={{
            fontSize: "10px",
            fontWeight: 400,
            color: isRedScheduleBox
              ? // Count is unknown until the query resolves; stay neutral so the
                // line doesn't flash red (its default) before turning green.
                submittedPlaylistsLoading
                ? theme.colors.gray
                : submittedCount > 0
                  ? theme.colors.green
                  : theme.colors.red
              : event.noShow
                ? theme.colors.gray
                : theme.colors.yellow,
          }}
        >
          {isRedScheduleBox ? renderSubmittedLabel() : event.playlistId}
        </div>
      </div>
    );
  };

  const eventPropGetter = (
    event: CalendarEvent
  ): { style: React.CSSProperties } => {
    if (isCuratorOwnEvent(event)) {
      return {
        style: {
          border: `1px solid ${theme.colors.yellow}`,
          backgroundColor: "rgba(255, 213, 0, 0.08)",
          boxShadow: `0 0 8px 1px rgba(255, 213, 0, 0.25)`,
          borderRadius: "4px",
        },
      };
    }
    return { style: {} };
  };

  const onSelectSlot = ({ start, end }: { start: Date; end: Date }): void => {
    if (roleType !== "admin" && roleType !== "manager") return;

    const formattedStartTime = moment(start).format("YYYY-MM-DDTHH:mm");
    const formattedEndTime = moment(end).format("YYYY-MM-DDTHH:mm");

    if (
      createEventDrawer &&
      createEventForm.startTime === formattedStartTime &&
      createEventForm.endTime === formattedEndTime
    ) {
      return;
    }

    const adjustedEndTime =
      moment(end).format("HH:mm") === "23:59"
        ? moment(end).add(1, "day").startOf("day").format("YYYY-MM-DDTHH:mm")
        : formattedEndTime;

    setCreateEventDrawer(false);

    dispatch(
      setCreateEventForm({
        startTime: formattedStartTime,
        endTime: adjustedEndTime,
      })
    );

    dispatch(resetUpdateEventForm());

    setTimeout(() => {
      setCreateEventDrawer(true);
    }, 200);
  };
  const onSelectEvent = (event: CalendarEvent): void => {
    if (roleType !== "admin" && roleType !== "manager") return;
    if (event.eventType !== "schedule") {
      dispatch(
        setCreateEventForm({
          showId: event.showDocumentId,
          playlistId: "",
          moodId: "",
        })
      );
      return onSelectSlot({ start: event.start, end: event.end });
    }

    const formattedStartTime = moment(event.start).format("YYYY-MM-DDTHH:mm");
    const formattedEndTime = moment(event.end).format("YYYY-MM-DDTHH:mm");

    if (
      createEventDrawer &&
      updateEventForm.startTime === formattedStartTime &&
      updateEventForm.endTime === formattedEndTime
    ) {
      return;
    }
    setCreateEventDrawer(false);
    const adjustedEndTime =
      moment(event.end).format("HH:mm") === "23:59"
        ? moment(event.end)
            .add(1, "day")
            .startOf("day")
            .format("YYYY-MM-DDTHH:mm")
        : formattedEndTime;
    dispatch(
      setUpdateEventForm({
        showId: event.showDocumentId,
        playlistId: event.playlistDocumentId,
        scheduleId: event.scheduleDocumentId,
        startTime: formattedStartTime,
        endTime: adjustedEndTime,
      })
    );
    dispatch(resetCreateEventForm());
    setTimeout(() => {
      setCreateEventDrawer(true);
    }, 200);
  };
  const toggleDrawer = () => {
    if (roleType === "admin" || roleType === "manager") {
      setCreateEventDrawer(!createEventDrawer);
    }
  };

  const weeks = Array.from(
    { length: FEATURES.SCHEDULE_CALENDAR_WEEKS },
    (_, i) => i + 1
  );

  if (roleType === "curator" && !showFullCalendar) {
    return (
      <CuratorScheduleView
        onShowFullCalendar={() => setShowFullCalendar(true)}
      />
    );
  }

  return (
    <WrapperContainer noScroll>
      <PageTopSection compact>
        <ScheduleOverviewHeader>
          <ScheduleOverviewHeaderLeft>
            <ScheduleTitleRow>
              <SchedulePageTitle>Schedule</SchedulePageTitle>
              <ScheduleOverviewHeaderCenter
                aria-busy={loading}
                $active={loading}
              >
                <Spinner small inline />
              </ScheduleOverviewHeaderCenter>
            </ScheduleTitleRow>
            <ButtonsContainer>
              {(roleType === "admin" || roleType === "manager") && (
                <ScheduleNewButton
                  $active
                  onClick={() => setCreateEventDrawer(true)}
                >
                  + New
                </ScheduleNewButton>
              )}
              {roleType === "curator" && (
                <Tooltip content="List View" position="bottom">
                  <ToggleViewButton
                    $matchPillSize
                    onClick={() => setShowFullCalendar(false)}
                  >
                    <img src={ListIcon} alt="list" /> List
                  </ToggleViewButton>
                </Tooltip>
              )}
              <WeeksContainer>
                {weeks.map((week, index) => (
                  <WeekButton
                    key={index}
                    onClick={() => setActiveWeek(index)}
                    active={weekIndexActive === index}
                  >
                    Week {week}
                  </WeekButton>
                ))}
              </WeeksContainer>
            </ButtonsContainer>
          </ScheduleOverviewHeaderLeft>
        </ScheduleOverviewHeader>
      </PageTopSection>
      <ContentContainer allowScroll>
        {(roleType === "admin" || roleType === "manager") && (
          <ContentDrawer isOpen={createEventDrawer} toggleDrawer={toggleDrawer}>
            <AddNewSchedule
              onClose={() => setCreateEventDrawer(false)}
              refetchEvents={() => {
                refetch();
                setCreateEventDrawer(false);
              }}
            />
          </ContentDrawer>
        )}
        <CalendarContainer>
          <Calendar
            events={events}
            localizer={localizer}
            startAccessor="start"
            endAccessor="end"
            // Suppress the browser's native title tooltip (e.g. "06:00 - 08:00:
            // Spring Reverb") so it doesn't collide with the show-details hover
            // tooltip. react-big-calendar builds the DOM title attribute from the
            // tooltipAccessor; the custom event renderer reads event.title
            // directly, so display is unaffected.
            tooltipAccessor={() => ""}
            defaultView="week"
            date={range.start}
            onNavigate={() => {}}
            timeslots={1}
            step={120}
            scrollToTime={scrollToTime}
            selectable={roleType === "admin" || roleType === "manager" || false}
            components={{
              toolbar: CustomToolbar,
              event: CustomEvent,
            }}
            eventPropGetter={eventPropGetter}
            formats={{
              dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                localizer
                  ? `${localizer.format(start, "MMM DD", culture)} - ${localizer.format(end, "MMM DD", culture)}`
                  : "",
            }}
            onSelectSlot={onSelectSlot}
            onSelectEvent={onSelectEvent}
            views={{
              week: true,
            }}
            selected={range.start}
            min={moment().startOf("day").toDate()}
            max={moment().add(1, "day").endOf("day").toDate()}
          />
        </CalendarContainer>
      </ContentContainer>
    </WrapperContainer>
  );
};

const CustomToolbar = ({ label }: { label: string }): React.JSX.Element => {
  return (
    <div className="rbc-toolbar">
      <span style={{ fontWeight: "bold" }} className="rbc-toolbar-label">
        {label}
      </span>
    </div>
  );
};

// Calendar slots are 2h blocks, so any start time maps to its block's start.
const getTimeslotKey = (start: moment.MomentInput): string => {
  const slotStart = moment(start).startOf("hour");
  return slotStart.hour(Math.floor(slotStart.hour() / 2) * 2).toISOString();
};

const generateUpComingEvents = (
  upComingEvents: CalendarEvent[],
  master: IMasterScheduleEvent,
  scheduledSlots: Set<string>,
  submittedPlaylists: { id: string; documentId: string }[] = []
): void => {
  const upComingEventString = master?.upcoming_schedules;
  if (upComingEventString) {
    upComingEventString.split("|").forEach((event) => {
      const upComingStart = moment(event).toDate();
      const upComingRawEnd = moment(event).add(2, "hours");
      const upComingEnd =
        upComingRawEnd.format("HH:mm") === "00:00"
          ? upComingRawEnd.subtract(1, "minute").toDate()
          : upComingRawEnd.toDate();
      if (moment(master.next_schedule).isSame(upComingStart)) return;
      if (!upComingStart || !upComingEnd) return;
      // A real schedule already owns this slot; a projection on top of it
      // renders the slot twice.
      if (scheduledSlots.has(getTimeslotKey(upComingStart))) return;
      upComingEvents.push({
        id: `master-${master.id}-${event}`,
        title: master.title || "Scheduled (Upcoming)",
        scheduleDocumentId: "",
        playlistDocumentId: "",
        showDocumentId: master?.document_id ?? "",
        curatorName: master?.curator ?? "",
        frequency: master?.frequency ?? null,
        playlistId: "",
        playlistTitle: "",
        noPlaylist: true,
        noShow: false,
        submittedPlaylistCount: submittedPlaylists.length,
        submittedPlaylistId:
          submittedPlaylists.length === 1 ? submittedPlaylists[0].id : "",
        submittedPlaylistDocumentId:
          submittedPlaylists.length === 1
            ? submittedPlaylists[0].documentId
            : "",
        start: upComingStart,
        end: upComingEnd,
        eventType: "masterSchedule" as "masterSchedule",
      });
    });
  }
};

export default ScheduleCalendar;
